import { useLanguage } from '@/contexts/LanguageContext';
import { motion, useInView, useMotionValue, useTransform, useReducedMotion, useScroll } from 'framer-motion';
import { useRef, useState } from 'react';
import { Mic, PhoneCall, FileText, Scan, BookOpen, ShieldCheck, Shield, HeartPulse, Lightbulb, ArrowRight, ChevronDown, LucideIcon } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const cards = [
  { name: 'Dr.Vet Voice Agent', path: '/voice-agent', Icon: Mic, descKey: 'voice', status: 'live' },
  { name: 'Dr.Vet Call Intelligence', path: '/call-intelligence', Icon: PhoneCall, descKey: 'call', status: 'live' },
  { name: 'Dr.Vet Medical Scribe', path: '/medical-scribe', Icon: FileText, descKey: 'scribe', status: 'testing' },
  { name: 'Dr.Vet X-Ray Intelligence', path: '/x-ray-intelligence', Icon: Scan, descKey: 'xray', status: 'live' },
  { name: 'Dr.Vet Knowledge Core', path: '/knowledge-core', Icon: BookOpen, descKey: 'knowledge', status: 'development' },
  { name: 'Dr.Vet Chief Auditor', path: '/chief-auditor', Icon: ShieldCheck, descKey: 'auditor', status: 'concept' },
  { name: 'Dr.Vet Reputation Shield', path: '/reputation-shield', Icon: Shield, descKey: 'reputation', status: 'live' },
  { name: 'SaveVets / SafePets', path: '/savevets-safepets', Icon: HeartPulse, descKey: 'savevets', status: 'active' },
  { name: 'Dr.Vet Idea Incubator', path: '/idea-incubator', Icon: Lightbulb, descKey: 'incubator', status: 'open' },
] as const;

const copy = {
  en: {
    title: 'Core Platform Modules',
    subtitle: 'Dr.Vet combines practical AI modules designed to support real veterinary workflows - from communication and documentation to structured clinical assistance.',
    learnMore: 'Learn more',
    explore: 'Open agent →',
    preview: 'Click to explore',
    ecosystemLink: 'Have an idea for a new agent? → Idea Incubator',
    showAll: 'Show all modules',
    showLess: 'Show less',
    statuses: {
      live: 'Live',
      testing: 'Testing',
      development: 'In development',
      concept: 'Concept',
      active: 'Active',
      open: 'Open for ideas',
    },
    descs: {
      voice: 'Records and transcribes every doctor–owner consultation in real time.',
      call: 'Transcribes incoming calls and scores operator quality on 12 criteria.',
      scribe: 'Generates structured medical records and differential diagnoses from transcripts.',
      xray: 'AI-assisted radiology reports in 10–20 seconds inside your team’s messenger app.',
      knowledge: 'Curator-controlled internal knowledge base for the entire clinic team.',
      auditor: 'Gently audits medical records using multi-model AI: Grok + Claude + GPT.',
      reputation: 'Evidence-based crisis pack for online hate - drafts public responses without exposing case details.',
      savevets: '14,907 animals rescued since 2022. $59 = lifetime protection. Verified by NGO SVBO.',
      incubator: 'Your idea can become the next Dr.Vet agent and help save the lives of thousands of animals.',
    },
  },
  uk: {
    title: 'Базові модулі платформи',
    subtitle: 'Dr.Vet об’єднує практичні AI-модулі, створені для підтримки реальних ветеринарних процесів - від комунікації та документації до структурованої клінічної допомоги.',
    learnMore: 'Дізнатися більше',
    explore: 'Відкрити агента →',
    preview: 'Натисніть, щоб переглянути',
    ecosystemLink: 'Маєте ідею нового агента? → Idea Incubator',
    showAll: 'Показати всі модулі',
    showLess: 'Згорнути',
    statuses: {
      live: 'Live',
      testing: 'Тестування',
      development: 'У розробці',
      concept: 'Концепція',
      active: 'Активний',
      open: 'Відкрито для ідей',
    },
    descs: {
      voice: 'Записує та транскрибує кожну консультацію лікар–власник у реальному часі.',
      call: 'Транскрибує вхідні дзвінки та оцінює якість оператора за 12 критеріями.',
      scribe: 'Створює структуровані медичні записи та диференційні діагнози з транскриптів.',
      xray: 'AI-звіти з рентгену за 10–20 секунд у месенджері вашої команди.',
      knowledge: 'Внутрішня база знань під контролем куратора для всієї команди клініки.',
      auditor: 'М’яко аудитує медичні записи з використанням Grok + Claude + GPT.',
      reputation: 'Доказовий кризовий пакет для онлайн-хейту - чернетки публічних відповідей без розкриття деталей кейсу.',
      savevets: '14 907 тварин врятовано з 2022 року. $59 = довічний захист. Верифіковано ГО SVBO.',
      incubator: 'Ваша ідея може стати новим агентом Dr.Vet і допомогти врятувати життя тисяч тварин.',
    },
  },
  ja: {
    title: 'コアプラットフォームモジュール',
    subtitle: 'Dr.Vetは、コミュニケーションや文書作成から構造化された臨床支援まで、実際の獣医ワークフローを支えるために設計された実用的なAIモジュールを統合します。',
    learnMore: '詳細を見る',
    explore: 'エージェントを開く →',
    preview: 'クリックして詳細を見る',
    ecosystemLink: '新しいエージェントのアイデアがありますか？ → Idea Incubator',
    showAll: 'すべてのモジュールを表示',
    showLess: '折りたたむ',
    statuses: {
      live: 'Live',
      testing: 'テスト中',
      development: '開発中',
      concept: 'コンセプト',
      active: 'アクティブ',
      open: 'アイデア募集中',
    },
    descs: {
      voice: '獣医師と飼い主の診察をリアルタイムで記録・文字起こし。',
      call: '着信通話を文字起こしし、12項目でオペレーター品質を評価。',
      scribe: '文字起こしから構造化された医療記録と鑑別診断を生成。',
      xray: 'チームのメッセンジャーアプリ内で10〜20秒のAI支援放射線レポート。',
      knowledge: 'キュレーター管理のクリニック全体向け内部知識ベース。',
      auditor: 'Grok + Claude + GPT のマルチモデルAIで医療記録を穏やかに監査。',
      reputation: 'SNS誹謗中傷への証拠ベース危機対応パック - 症例詳細を出さない公開文の下書きを生成。',
      savevets: '2022年以降14,907頭を救済。$59で生涯保護。NGO SVBO認証。',
      incubator: 'あなたのアイデアが次のDr.Vetエージェントとなり、何千もの動物の命を救うかもしれません。',
    },
  },
};

type CardProps = {
  name: string;
  path: string;
  Icon: LucideIcon;
  desc: string;
  learnMore: string;
  preview: string;
  index: number;
  langSuffix: string;
  statusLabel?: string;
  statusKey?: string;
};

function AgentCard({ name, path, Icon, desc, learnMore, preview, index, langSuffix, statusLabel, statusKey }: CardProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLAnchorElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  // Parallax: small Y shift based on scroll progress (alternating per column)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const parallaxRange = (index % 2 === 0 ? -8 : 8);
  const parallaxY = useTransform(scrollYProgress, [0, 1], [parallaxRange, -parallaxRange]);

  // 3D tilt
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [6, -6]);
  const rotateY = useTransform(mx, [0, 1], [-6, 6]);

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (reduce) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };
  const handleMouseLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <motion.a
      ref={ref}
      href={`${path}${langSuffix}`}
      onClick={() => trackEvent('agent_explore_click', { agent: name, page: 'home' })}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 30, scale: 0.94 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{
        type: 'spring',
        stiffness: 120,
        damping: 16,
        delay: reduce ? 0 : (index % 4) * 0.08,
      }}
      style={{
        y: reduce ? 0 : parallaxY,
        rotateX: reduce ? 0 : rotateX,
        rotateY: reduce ? 0 : rotateY,
        transformStyle: 'preserve-3d',
        perspective: 1000,
      }}
      whileHover={reduce ? undefined : { y: -6 }}
      className="relative card-elevated p-6 group flex flex-col cursor-pointer overflow-hidden border-2 border-transparent hover:border-primary/60 hover:shadow-xl hover:shadow-primary/10 transition-[box-shadow,border-color] duration-300"
    >
      {/* Rotating conic gradient border on first appearance */}
      {!reduce && (
        <motion.span
          aria-hidden
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: [0, 0.7, 0] } : {}}
          transition={{ duration: 1.6, ease: 'easeInOut' }}
          className="pointer-events-none absolute -inset-[2px] rounded-xl"
          style={{
            background:
              'conic-gradient(from 0deg, transparent 0deg, hsl(var(--primary)/0.6) 90deg, transparent 180deg, hsl(var(--primary)/0.4) 270deg, transparent 360deg)',
            WebkitMask:
              'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            padding: 2,
            animation: inView ? 'spin 1.5s linear' : undefined,
          }}
        />
      )}

      {/* Top accent line on hover (kept) */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/0 via-primary to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Diagonal sweep on appear */}
      {!reduce && (
        <motion.div
          aria-hidden
          initial={{ x: '-120%' }}
          animate={inView ? { x: '120%' } : {}}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
          className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/2 skew-x-[-20deg]"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, hsl(var(--primary)/0.12) 50%, transparent 100%)',
          }}
        />
      )}

      {/* Icon wrapper with float + pulse ring */}
      <div className="relative w-12 h-12 mb-4">
        {!reduce && (
          <motion.span
            aria-hidden
            initial={{ scale: 0, opacity: 0.6 }}
            animate={inView ? { scale: 2, opacity: 0 } : {}}
            transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
            className="absolute inset-0 rounded-xl border-2 border-primary"
          />
        )}
        <motion.div
          animate={reduce ? {} : { y: [0, -3, 0] }}
          transition={{ duration: 4, ease: 'easeInOut', repeat: Infinity }}
          className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300"
          style={{ transform: 'translateZ(20px)' }}
        >
          <Icon size={22} className="text-primary" />
        </motion.div>
      </div>

      <div className="flex items-start justify-between gap-2 mb-2" style={{ transform: 'translateZ(15px)' }}>
        <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">{name}</h3>
        {statusLabel && (
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
              statusKey === 'live'
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                : statusKey === 'testing'
                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                : statusKey === 'development'
                ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/30'
                : statusKey === 'concept'
                ? 'bg-muted text-muted-foreground border-border'
                : 'bg-primary/10 text-primary border-primary/30'
            }`}
          >
            {statusLabel}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{desc}</p>
      <div className="flex items-center justify-between pt-3 border-t border-border/40">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
          {learnMore}
          <motion.span
            animate={reduce ? {} : { x: [0, 2, 0] }}
            transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity }}
            className="inline-flex"
          >
            <ArrowRight size={14} />
          </motion.span>
        </span>
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity">
          {preview}
        </span>
      </div>
    </motion.a>
  );
}

export default function AgentsGridSection() {
  const { lang } = useLanguage();
  const c = copy[lang];
  const langSuffix = lang === 'en' ? '' : `?lang=${lang}`;
  const [expanded, setExpanded] = useState(false);
  const visibleCards = expanded ? cards : cards.slice(0, 6);

  return (
    <section id="agents" className="section-padding section-alt">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-5 tracking-tight">
            {c.title}
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {c.subtitle}
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {visibleCards.map((card, i) => {
            const status = 'status' in card ? (card as { status?: keyof typeof c.statuses }).status : undefined;
            return (
              <AgentCard
                key={card.path}
                name={card.name}
                path={card.path}
                Icon={card.Icon}
                desc={c.descs[card.descKey]}
                learnMore={c.learnMore}
                preview={c.preview}
                index={i}
                langSuffix={langSuffix}
                statusLabel={status ? c.statuses[status] : undefined}
                statusKey={status}
              />
            );
          })}
        </div>
        <div className="text-center mt-10 flex flex-col items-center gap-5">
          <button
            type="button"
            onClick={() => {
              setExpanded((v) => !v);
              trackEvent('agents_toggle_expand', { expanded: !expanded });
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary text-sm md:text-base font-semibold text-primary transition-all"
            aria-expanded={expanded}
          >
            {expanded ? c.showLess : c.showAll}
            <ChevronDown
              size={16}
              className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
          <a
            href={`/idea-incubator${langSuffix}`}
            className="inline-flex items-center gap-1.5 text-sm md:text-base font-semibold text-primary hover:underline"
          >
            {c.ecosystemLink}
          </a>
        </div>
      </div>
    </section>
  );
}
